import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import { DOCUMENT_TITLE_WRAP_CLASS } from '../../../utils/documentTitle';

/* =======================
   HELPERS
======================= */

const STATUS_CONFIG = {
  DRAFT: {
    label: 'Rascunho',
    color: 'text-warning',
    bgColor: 'bg-warning/10',
    icon: 'Edit',
  },
  PDF_GENERATED: {
    label: 'PDF Gerado',
    color: 'text-success',
    bgColor: 'bg-success/10',
    icon: 'CheckCircle',
  },
  PDF_UPLOADED: {
    label: 'PDF enviado',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-500/15',
    icon: 'Upload',
  },
  PENDING_SIGNATURE: {
    label: 'Aguardando assinatura',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    icon: 'Clock',
  },
  SIGNED: {
    label: 'Assinado por todos',
    color: 'text-foreground',
    bgColor: 'bg-amber-500/15',
    icon: 'FileCheck',
  },
  ERROR: {
    label: 'Falha',
    color: 'text-error',
    bgColor: 'bg-error/10',
    icon: 'AlertTriangle',
  },
  MANUAL: {
    label: 'Manual',
    color: 'text-violet-700 dark:text-violet-300',
    bgColor: 'bg-violet-500/15',
    icon: 'FilePenLine',
  },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatSignatureDate = (value) => {
  if (value == null || value === '') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const DOUBLE_TAP_MS = 350;

function isInteractiveEventTarget(el) {
  return Boolean(el?.closest?.('button,a,[role="button"],input,textarea,select'));
}

/* =======================
   COMPONENT
======================= */

const ContractTableRow = ({
  contract,
  onDownload,
  onEdit,
  onDelete,
  onSendToSignature,
  onDuplicateContract,
  onCancelSignature,
  onViewSignatureStatus,
  onGeneratePdf,
  onViewPdf,
  isGeneratingPdf = false,
}) => {
  const status = STATUS_CONFIG[contract.status] || STATUS_CONFIG.DRAFT;
  const isPendingSignature = contract.status === 'PENDING_SIGNATURE';
  const isSigned = contract.status === 'SIGNED';
  const isPdfGenerated = contract.status === 'PDF_GENERATED';
  const isManualDoc = Boolean(contract.is_manual);
  const isUploadedSignature = Boolean(contract.is_manual_signature);

  const canEditTemplate =
    contract.status === 'DRAFT' ||
    contract.status === 'PDF_GENERATED' ||
    contract.status === 'MANUAL';

  const canEdit = isUploadedSignature
    ? false
    : isManualDoc
      ? Boolean(contract.job_id) && !isSigned && !isPendingSignature
      : canEditTemplate;

  const canDownload = isUploadedSignature
    ? contract.status === 'PDF_UPLOADED' ||
      contract.status === 'PDF_GENERATED' ||
      isPendingSignature ||
      isSigned
    : isPdfGenerated || isSigned;

  const canViewPdf = isUploadedSignature
    ? canDownload
    : isPdfGenerated;

  const canDelete = true;
  const canSendToSignature = isUploadedSignature
    ? contract.status === 'PDF_UPLOADED' ||
      contract.status === 'PDF_GENERATED' ||
      isPendingSignature
    : isPdfGenerated || isPendingSignature;
  const canGeneratePdf =
    !isManualDoc &&
    !isUploadedSignature &&
    Boolean(contract.job_id) &&
    (contract.status === 'DRAFT' || contract.status === 'PDF_GENERATED');

  const lastTouchEndMsRef = useRef(0);

  const handlesDoubleEdit = (target) =>
    Boolean(canEdit) && !isInteractiveEventTarget(target);

  const handleRowDoubleClick = (e) => {
    if (!handlesDoubleEdit(e.target)) return;
    onEdit(contract);
  };

  const handleRowTouchEnd = (e) => {
    if (!handlesDoubleEdit(e.target)) return;
    const now = Date.now();
    if (
      now - lastTouchEndMsRef.current < DOUBLE_TAP_MS &&
      lastTouchEndMsRef.current > 0
    ) {
      lastTouchEndMsRef.current = 0;
      if (typeof e.cancelable === 'boolean' && e.cancelable) e.preventDefault();
      onEdit(contract);
    } else {
      lastTouchEndMsRef.current = now;
    }
  };

  return (
    <tr
      className={`list-item-hover ${canEdit ? 'cursor-pointer' : ''} ${
        isUploadedSignature
          ? 'border-l-4 border-primary/40'
          : isManualDoc
            ? 'border-l-4 border-violet-500'
            : ''
      } ${
        isSigned
          ? 'bg-amber-500/5'
          : isManualDoc
            ? 'bg-violet-500/[0.06]'
            : ''
      }`}
      onDoubleClick={handleRowDoubleClick}
      onTouchEnd={handleRowTouchEnd}
      title={canEdit ? 'Clique duas vezes para editar' : undefined}
    >
      {/* CONTRATO */}
      <td className="px-4 md:px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${isManualDoc ? 'bg-violet-500/15' : 'bg-primary/10'
              }`}
          >
            <Icon
              name={isManualDoc ? 'PenLine' : 'FileText'}
              size={20}
              className={isManualDoc ? 'text-violet-600 dark:text-violet-400' : 'text-primary'}
            />
          </div>

          <div className="min-w-0">
            <p
              className={cn(
                'text-sm font-medium text-foreground font-body',
                DOCUMENT_TITLE_WRAP_CLASS
              )}
              title={contract.name}
            >
              {contract.name}
            </p>
            <div className="text-xs text-muted-foreground font-caption space-y-0.5">
              {isUploadedSignature ? (
                <p className="truncate">
                  <span className="text-primary font-medium">Documento enviado (upload)</span>
                </p>
              ) : isManualDoc ? (
                <>
                  <p className="truncate">
                    <span className="text-violet-700 dark:text-violet-300 font-medium">
                      Documento manual
                    </span>
                  </p>
                  {contract.type ? (
                    <p className="truncate text-[11px] opacity-80">{contract.type}</p>
                  ) : null}
                </>
              ) : (
                <p className="truncate">{contract.type}</p>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* DATA */}
      <td className="px-4 md:px-6 py-4 hidden md:table-cell">
        <p className="text-sm text-foreground font-body">
          {formatDateTime(contract.createdAt)}
        </p>
      </td>

      {/* STATUS */}
      <td className="px-4 md:px-6 py-4">
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium font-caption ${status.bgColor} ${status.color}`}
        >
          <Icon name={status.icon} size={14} />
          {status.label}
        </span>
      </td>

      {/* DATA ASSINATURA */}
      <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
        <p className="text-sm text-foreground font-body">
          {formatSignatureDate(contract.signatureDate)}
        </p>
      </td>

      {/* AÇÕES (VALUE ONLY) */}
      <td className="px-4 md:px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {isPendingSignature ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onViewSignatureStatus?.(contract)}
                className="transition-transform hover:scale-110"
                aria-label="Status das assinaturas"
                title="Status das assinaturas"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Ban"
                onClick={() => onCancelSignature?.(contract)}
                className="transition-transform hover:scale-110"
                aria-label="Cancelar assinatura"
                title="Cancelar assinatura"
              />
            </>
          ) : isSigned ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onViewSignatureStatus?.(contract)}
                className="transition-transform hover:scale-110"
                aria-label="Status das assinaturas"
                title="Status das assinaturas"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownload(contract)}
                disabled={!canDownload}
                className="transition-transform hover:scale-110"
                aria-label="Baixar documento"
                title="Baixar documento"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(contract)}
                className="text-destructive hover:text-destructive transition-transform hover:scale-110"
                aria-label="Excluir documento"
                title="Excluir documento"
              />
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onViewPdf?.(contract)}
                disabled={!canViewPdf}
                className="transition-transform hover:scale-110"
                aria-label="Visualizar PDF"
                title="Visualizar PDF"
              />

              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownload(contract)}
                disabled={!canDownload}
                className="transition-transform hover:scale-110"
                aria-label="Baixar documento"
                title="Baixar documento"
              />

              <Button
                variant="ghost"
                size="sm"
                iconName="Edit"
                onClick={() => onEdit(contract)}
                disabled={!canEdit}
                className="transition-transform hover:scale-110"
                aria-label="Editar documento"
                title="Editar documento"
              />

              {canGeneratePdf && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="FileText"
                  onClick={() => onGeneratePdf?.(contract)}
                  disabled={isGeneratingPdf}
                  loading={isGeneratingPdf}
                  className="transition-transform hover:scale-110"
                  aria-label="Gerar Pdf"
                  title="Gerar Pdf"
                />
              )}

              <Button
                variant="ghost"
                size="sm"
                iconName="PenTool"
                onClick={() => onSendToSignature?.(contract)}
                disabled={!canSendToSignature}
                className="transition-transform hover:scale-110"
                aria-label="Posicionar campos de assinatura"
                title="Posicionar campos de assinatura"
              />
              {isManualDoc && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Copy"
                  onClick={() => onDuplicateContract?.(contract)}
                  className="transition-transform hover:scale-110"
                  aria-label="Duplicar documento"
                  title="Duplicar documento"
                />
              )}

              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(contract)}
                className="text-destructive hover:text-destructive transition-transform hover:scale-110"
                aria-label="Excluir documento"
                title="Excluir documento"
              />
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ContractTableRow;
